const { Criteria } = Shopware.Data;
const { Mixin } = Shopware;

export default {
    template: `
<sw-page class="riccardo-magazine-detail">
    <template #smart-bar-header><h2>{{ post ? (post.title || 'Neuer Beitrag') : '' }}</h2></template>
    <template #smart-bar-actions>
        <sw-button v-if="postId && post && post.slug" @click="openInShop">Im Shop ansehen</sw-button>
        <sw-button :router-link="{ name: 'riccardo.magazine.list' }">Abbrechen</sw-button>
        <sw-button-process variant="primary" :is-loading="isLoading" :process-success="isSaveSuccessful" @click="onSave">Speichern</sw-button-process>
    </template>
    <template #content>
        <sw-card-view v-if="post">
            <sw-card title="Beitrag" :is-loading="isLoading">
                <sw-text-field v-model:value="post.title" label="Titel" required @update:value="onTitleChange" />
                <sw-container columns="2fr 1fr 1fr" gap="0 24px">
                    <sw-text-field v-model:value="post.slug" label="URL-Kürzel" help-text="Adresse im Shop: /magazin/<Kürzel>" required />
                    <sw-datepicker v-model:value="post.publishedAt" date-type="datetime" label="Veröffentlicht am" />
                    <sw-number-field v-model:value="post.readingMinutes" number-type="int" label="Lesezeit (Min.)"
                                     help-text="Leer lassen: wird beim Speichern aus der Textlänge gerechnet." />
                </sw-container>
                <sw-container columns="repeat(3, 1fr)" gap="0 24px">
                    <sw-switch-field v-model:value="post.active" label="Aktiv" />
                    <sw-switch-field v-model:value="post.pinned" label="Als Empfehlung hervorheben" />
                    <sw-text-field v-model:value="post.author" label="Autor" placeholder="Riccardo Redaktion" />
                </sw-container>
                <sw-entity-multi-select v-model:entity-collection="post.categories" entity="riccardo_magazine_category"
                                        label="Themen" :label-property="'name'" />
            </sw-card>

            <sw-card title="Titelbild" :is-loading="isLoading">
                <sw-upload-listener :upload-tag="uploadTag" auto-upload @media-upload-finish="onMediaUpload" />
                <sw-media-upload-v2 :upload-tag="uploadTag" :source="post.mediaId" label="Bild für Übersicht und Beitrag"
                                    default-folder="cms_page" @media-drop="onMediaDrop" @media-upload-remove-image="post.mediaId = null" />
            </sw-card>

            <sw-card title="Anrisstext" :is-loading="isLoading">
                <sw-textarea-field v-model:value="post.teaser" label="Kurzfassung für die Übersicht"
                                   help-text="Zwei bis drei Sätze. Erscheint auch als Beschreibung bei Google." />
            </sw-card>

            <sw-card title="Inhalt" :is-loading="isLoading">
                <sw-text-editor v-model:value="post.content" label="Beitrag" :allow-inline-data-mapping="false" />
            </sw-card>

            <sw-card title="Suchmaschinen" :is-loading="isLoading">
                <sw-text-field v-model:value="post.metaTitle" label="Meta-Titel" :placeholder="post.title" />
                <sw-textarea-field v-model:value="post.metaDescription" label="Meta-Beschreibung" :placeholder="post.teaser" />
            </sw-card>
        </sw-card-view>
    </template>
</sw-page>`,

    inject: ['repositoryFactory'],

    mixins: [Mixin.getByName('notification')],

    props: {
        postId: { type: String, required: false, default: null },
    },

    data() {
        return { post: null, isLoading: false, isSaveSuccessful: false, uploadTag: 'riccardo-magazine-media' };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('riccardo_magazine_post');
        },
    },

    created() {
        this.load();
    },

    methods: {
        load() {
            if (!this.postId) {
                this.post = this.repository.create();
                this.post.active = true;
                this.post.pinned = false;
                this.post.author = 'Riccardo Redaktion';
                this.post.publishedAt = new Date().toISOString();
                return Promise.resolve();
            }
            this.isLoading = true;
            const criteria = new Criteria();
            criteria.addAssociation('categories');
            return this.repository.get(this.postId, Shopware.Context.api, criteria).then((post) => {
                this.post = post;
            }).finally(() => { this.isLoading = false; });
        },

        onTitleChange(value) {
            if (this.post.slug || !value) {
                return;
            }
            this.post.slug = value.toLowerCase()
                .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
                .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 120);
        },

        onMediaUpload({ targetId }) {
            this.post.mediaId = targetId;
        },
        onMediaDrop(media) {
            this.post.mediaId = media.id;
        },
        openInShop() {
            window.open(`/magazin/${this.post.slug}`, '_blank', 'noopener');
        },

        onSave() {
            if (!this.post.readingMinutes && this.post.content) {
                const words = this.post.content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
                this.post.readingMinutes = Math.max(1, Math.round(words / 200));
            }
            this.isLoading = true;
            this.isSaveSuccessful = false;
            return this.repository.save(this.post).then(() => {
                this.isSaveSuccessful = true;
                this.createNotificationSuccess({ message: 'Beitrag gespeichert' });
                if (!this.postId) {
                    this.$router.push({ name: 'riccardo.magazine.detail', params: { id: this.post.id } });
                    return Promise.resolve();
                }
                return this.load();
            }).catch((error) => {
                const code = error?.response?.data?.errors?.[0]?.code || '';
                this.createNotificationError({ message: code === '1062' ? 'Dieses URL-Kürzel ist schon vergeben.' : 'Speichern fehlgeschlagen.' });
            }).finally(() => { this.isLoading = false; });
        },
    },
};
